import ms from 'ms';
import { useCallback, useContext } from 'react';
import { useContextSelector } from 'use-context-selector';

import { logger } from 'config/logger';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useGenerateEnrichedVersionMutation } from 'shared/legacy/hooks/useGenerateEnrichedVersionMutation';
import { useGenerateJobPreviewMutation } from 'shared/legacy/hooks/useGenerateJobPreviewMutation';
import { useSnackbarStore } from 'stores/snackbar/snackbar.store';
import { mapJobImprovedGeneratedByPreviewToJobFormValues } from '../../utils/EnrichVacancyModal.mappers';
import { EditEnrichedVacancyContext } from './EditEnrichedVacancyContext/EditEnrichedVacancyContext';

export const useGenerate = () => {
  const enrichedVersionRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.enrichedVersion.ref);
  const jobPreviewRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.jobPreview.ref);
  const setJobPreviewInitialData = useContextSelector(EditEnrichedVacancyContext, (s) => s.setJobPreviewInitialData);
  const setEnrichedVersionInitialData = useContextSelector(
    EditEnrichedVacancyContext,
    (s) => s.setEnrichedVersionInitialData,
  );
  const { dictionary } = useContext(LocalizationContext);

  const { mutateAsync: generateJobPreview } = useGenerateJobPreviewMutation();
  const { mutateAsync: generateEnrichedVacancy } = useGenerateEnrichedVersionMutation();

  const handleGenerateJobPreview = useCallback(async (): Promise<string> => {
    const formData = enrichedVersionRef.current?.getValues();

    if (!formData) return '';

    try {
      const generatedJobPreview = await generateJobPreview(formData);
      enrichedVersionRef.current?.reset(formData);

      setJobPreviewInitialData(generatedJobPreview);

      return generatedJobPreview;
    } catch (error) {
      logError(error, dictionary.somethingWentWrong);

      return '';
    }
  }, [dictionary]);

  const handleGenerateEnrichedVacancy = useCallback(async () => {
    const jobPreviewValue = jobPreviewRef.current?.getValue() ?? '';
    const jobPreviewHTML = jobPreviewRef.current?.getHTML() ?? '';

    if (!jobPreviewValue) return;

    try {
      const jobImproved = await generateEnrichedVacancy(jobPreviewValue);

      const formData = mapJobImprovedGeneratedByPreviewToJobFormValues(jobImproved);

      jobPreviewRef.current?.reset(jobPreviewHTML);
      setEnrichedVersionInitialData(formData);

      return formData;
    } catch (error) {
      logError(error, dictionary.somethingWentWrong);
    }
  }, [dictionary]);

  return { handleGenerateJobPreview, handleGenerateEnrichedVacancy };
};

const logError = (error: unknown, fallbackMessage: string) => {
  logger.error(error);

  useSnackbarStore.getState().createSnackbar(error instanceof Error ? error.message : fallbackMessage, {
    variant: 'danger',
    duration: ms('10s'),
  });
};
